/** @format */

import PinIcon from '@/assets/joivy_poi_miniature_pink.svg';
import { NeighborhoodLink } from '@/components/shared/NeighborhoodLink';
import { UILoader } from '@/components/ui/Loader';
import { selectorIsLoadingSearch } from '@/ducks/general/selectors';
import {
  GTMPDPMapInteractionAddressClick,
  GTMPDPMapInteractionZoom,
  GTMPDPNeighborhoodClick,
} from '@/ducks/googleTagManager/helpers/google-tag-manager';
import { TRoom } from '@/ducks/room/declarations';
import { useInViewPort } from '@/ducks/room/hooks/useInViewPort';
import { useAppSelector } from '@/redux/hooks';

import { MapRoomLocation } from '@/components/shared/MapRoomLocation';
import { utilityGetPrice } from '@/utils/helpers/utilityGetPrice';
import { FormattedMessage } from 'gatsby-plugin-intl';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { memo, useEffect, useRef, useState } from 'react';

import { TTranslation, useAppTranslation } from '@/i18n/declarations';
import styled from 'styled-components';
import SwiperCore, { Controller, Mousewheel, Virtual } from 'swiper';
import { CustomTitle, WrapperInfoPosition } from '../../index.style';
import { caseUnsensitiveEquals } from '@/utils/helpers/caseUnsensitiveEquals';
import { RoomAvailability } from '../../../../../@ducks/bookingNew/types';
import snakeCase from 'lodash/snakeCase';

// @ts-ignore
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

SwiperCore.use([Controller, Mousewheel, Virtual]);

interface Props {
  room: TRoom;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  filteredAvailabilityArray: Array<RoomAvailability>;
  url: string | null;
}

const MapDesktop = memo(({ room, coordinates, filteredAvailabilityArray, url }: Props) => {
  const { t } = useAppTranslation();
  const mapRef = useRef(null);
  const isVisible = useInViewPort(mapRef);
  const [showMap, setShowMap] = useState(false);
  const isRoomLoading = useAppSelector(selectorIsLoadingSearch);
  const [viewport, setViewport] = useState({
    latitude: coordinates?.latitude,
    longitude: coordinates?.longitude,
    zoom: 10,
    transitionDuration: '' as any,
  });
  const zoomInteractionTimer = useRef<NodeJS.Timer>();
  const [currentZoom, setCurrentZoom] = useState(10);

  const roomAvailability = (roomCode: string) => {
    const availabilityFound = filteredAvailabilityArray.find(el => caseUnsensitiveEquals(el?.bedCode, roomCode));
    return availabilityFound;
  };

  useEffect(() => {
    setViewport({
      ...viewport,
      latitude: room.position.latitude,
      longitude: room.position.longitude,
      zoom: 13.7,
      transitionDuration: 'auto',
    });
  }, [room]);

  useEffect(() => {
    if (isVisible) {
      setShowMap(true);
    }
  }, [isVisible]);

  const handleGTMAddressClick = () => {
    const { getFormattedPrice } = utilityGetPrice({
      availability: roomAvailability(room.code),
      room,
    });

    GTMPDPMapInteractionAddressClick(room.code, getFormattedPrice());
  };

  const handleGTMZoom = async (zoom: number) => {
    // debouncing the action since a single click on the zoom buttons generates many calls to this function
    if (zoomInteractionTimer.current) {
      clearTimeout(zoomInteractionTimer.current);
    }
    zoomInteractionTimer.current = setTimeout(() => {
      const { getFormattedPrice } = utilityGetPrice({
        availability: roomAvailability(room.code),
        room: room!,
      });

      GTMPDPMapInteractionZoom(zoom > currentZoom ? 'zoom_in' : 'zoom_out', room.code, getFormattedPrice());
      setCurrentZoom(zoom);
      clearTimeout(zoomInteractionTimer.current);
    }, 100);
  };

  const handleGTMPDPNeighborhoodClick = () => {
    const { getFormattedPrice } = utilityGetPrice({
      availability: roomAvailability(room.code),
      room,
    });

    const parts = url.split('/');
    const lastPart = parts[parts.length - 1].replace(`${t('url_discover')}-`, '');

    GTMPDPNeighborhoodClick(room.code, getFormattedPrice(), snakeCase(lastPart));
  };

  return (
    <MapDesktopContainer ref={mapRef}>
      {showMap && (
        <>
          {isRoomLoading && <UILoader />}
          <CustomTitle $subtitle={true}>
            <FormattedMessage id="pdp.where_you_will_be" />
          </CustomTitle>
          <WrapperInfoPosition>
            <img src={PinIcon} alt="location" width={17} />
            <p>
              {t(room.city.nameEn.toLowerCase().replaceAll(' ', '_') as TTranslation)} <span>|</span>
            </p>
            <a
              onClick={handleGTMAddressClick}
              target="_blank"
              href={`https://maps.google.com/?q=${room.position.latitude},${room.position.longitude}`}
              rel="noreferrer nofollow"
            >
              {room.address.toLowerCase()}
            </a>
            {!!url && <NeighborhoodLink onClick={handleGTMPDPNeighborhoodClick} url={url} />}
          </WrapperInfoPosition>
          <MapDesktopMapWrapper>
            <MapDesktopMapContainer>
              {room && (
                <MapRoomLocation
                  room={room}
                  onViewStateChange={(e: { viewState: { zoom: number } }) => handleGTMZoom(e.viewState.zoom)}
                />
              )}
            </MapDesktopMapContainer>
          </MapDesktopMapWrapper>
        </>
      )}
    </MapDesktopContainer>
  );
});

const MapDesktopMapWrapper = styled.div`
  position: relative;
  border-radius: 0;
  overflow: hidden;
  height: 100%;
  width: 100%;
  max-height: 350px;

  &-top {
    position: absolute;
    left: 48px;
    right: 48px;
    top: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;
    button {
      outline: none;
      border: none;
      background-color: ${({ theme }) => theme.colors.joivy.white};
      border-radius: 50px;
      font-family: ${({ theme }) => theme.font.body};
      font-style: normal;
      font-weight: 400;
      font-size: 15px;
      line-height: 140%;
      color: ${({ theme }) => theme.colors.joivy.black};
      padding: 16px 24px;
      pointer-events: all;
      cursor: pointer;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
      transition: 0.15s box-shadow;
      &:hover {
        box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.16);
      }
    }
  }
`;

const MapDesktopMapContainer = styled.div`
  width: 100%;
  height: 100%;
  max-height: 350px;
  display: flex;
  flex-direction: column;
  border-radius: 1.875rem;
  overflow: hidden;
`;

const MapDesktopContainer = styled.div`
  height: 462px;
  width: 100%;
  position: relative;
  overflow-y: hidden;
`;

export default MapDesktop;
